import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { TransactionListItemDto } from "../../../common/types/transaction";
import { StandardModal } from "../../general/Modal/Modal";
import { TransferMatchingContainer } from "../TransferMatching/TransferMatchingContainer";

export interface TransferMatchModalContextType {
    openTransferMatch: (transaction: TransactionListItemDto) => void;
}

export const TransferMatchModalContext = createContext<
    TransferMatchModalContextType | undefined
>(undefined);

export const useTransferMatchModal = () => {
    const ctx = useContext(TransferMatchModalContext);
    if (!ctx) {
        throw new Error(
            "useTransferMatchModal must be used within a TransferMatchModalProvider",
        );
    }
    return ctx;
};

export const TransferMatchModalProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const [openedTransaction, setOpenedTransaction] =
        useState<TransactionListItemDto | null>(null);

    const openTransferMatch = useCallback(
        (transaction: TransactionListItemDto) => {
            setOpenedTransaction(transaction);
        },
        [],
    );

    const handleClose = useCallback(() => {
        setOpenedTransaction(null);
    }, []);

    const contextValue = useMemo(
        () => ({
            openTransferMatch,
        }),
        [openTransferMatch],
    );

    return (
        <TransferMatchModalContext.Provider value={contextValue}>
            {children}
            <StandardModal
                size="xl"
                className="fullscreen-modal fullscreen-modal--expanded"
                show={Boolean(openedTransaction)}
                onHide={handleClose}
            >
                {openedTransaction && (
                    <TransferMatchingContainer
                        key={openedTransaction.id}
                        transactionId={openedTransaction.id}
                        transaction={openedTransaction}
                    />
                )}
            </StandardModal>
        </TransferMatchModalContext.Provider>
    );
};
